import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Organization, MenuItem, MenuCategory } from '../../types';
import { apiService } from '../../services/apiService';
import { DEMO_ORGANIZATION, isDemoOrg } from '../../data/demoData';
import './PublicPage.css';

interface PublicPageProps {
  orgSlug?: string;
}

const PublicPage: React.FC<PublicPageProps> = ({ orgSlug: propOrgSlug }) => {
  const { shopSlug: paramShopSlug } = useParams<{ shopSlug: string }>();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Use prop orgSlug first, then fall back to param shopSlug
  const orgSlug = propOrgSlug || paramShopSlug;
  
  // Debug logging

  useEffect(() => {
    const loadOrganizationAndMenu = async () => {
      if (orgSlug) {
        try {
          // Check if this is the demo organization
          if (isDemoOrg(orgSlug)) {
            // For demo org, use demo data
            const menuResponse = await apiService.getPublicMenu(orgSlug);
            if (menuResponse.success && menuResponse.data?.items) {
              setMenuItems(menuResponse.data.items);
            } else {
              setMenuItems([]);
            }
            
            // Load demo categories
            const categoriesResponse = await apiService.getPublicCategories(orgSlug);
            if (categoriesResponse.success && categoriesResponse.data?.categories) {
              setMenuCategories(categoriesResponse.data.categories);
            } else {
              setMenuCategories([]);
            }
            
            // Use centralized demo organization data
            setOrganization(DEMO_ORGANIZATION);
            setLoading(false);
          } else {
            // For regular orgs, get public organization details
            const orgResponse = await apiService.getPublicOrganization(orgSlug);
            if (!orgResponse.success || !orgResponse.data?.organization) {
              console.error('Failed to load public organization:', orgResponse.error);
              setLoading(false);
              return;
            }

            const org = orgResponse.data.organization;
            setOrganization(org);
            
            // Load the public menu for this organization
            const menuResponse = await apiService.getPublicMenu(orgSlug);
            if (menuResponse.success && menuResponse.data?.items) {
              setMenuItems(menuResponse.data.items);
            } else {
              console.error('Failed to load public menu:', menuResponse.error);
              setMenuItems([]);
            }

            // Load categories for this organization
            const categoriesResponse = await apiService.getPublicCategories(orgSlug);
            if (categoriesResponse.success && categoriesResponse.data?.categories) {
              setMenuCategories(categoriesResponse.data.categories);
            } else {
              console.error('Failed to load menu categories:', categoriesResponse.error);
              setMenuCategories([]);
            }

            setLoading(false);
          }
        } catch (error) {
          console.error('Failed to load organization and menu:', error);
          setLoading(false);
        }
      }
    };

    loadOrganizationAndMenu();
  }, [orgSlug]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner-text">
            {isDemoOrg(orgSlug || '') ? 'Loading demo data...' : 'Loading organization...'}
          </div>
          <div className="loading-spinner-element"></div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="public-page">
        <div className="shop-not-found">
          <h1>Organization not found</h1>
          <p>The organization "{orgSlug}" does not exist.</p>
        </div>
      </div>
    );
  }

  // Default cafe info for display
  const cafeInfo = {
    name: organization.name,
    description: `Welcome to ${organization.name} - Your favorite coffee destination`,
    hours: {
      monday: '7:00 AM - 9:00 PM',
      tuesday: '7:00 AM - 9:00 PM',
      wednesday: '7:00 AM - 9:00 PM',
      thursday: '7:00 AM - 9:00 PM',
      friday: '7:00 AM - 10:00 PM',
      saturday: '8:00 AM - 10:00 PM',
      sunday: '8:00 AM - 8:00 PM'
    },
    location: '123 Coffee Street, Your City',
    phone: '(555) 123-4567',
    email: `info@${organization.urlName}.com`
  };
  
  const menuByCategory = menuItems.reduce((acc, item) => {
    const categoryKey = item.categoryId?.toString() || 'uncategorized';
    if (!acc[categoryKey]) {
      acc[categoryKey] = [];
    }
    acc[categoryKey].push(item);
    return acc;
  }, {} as Record<string, typeof menuItems>);

  // Create a map of category IDs to category names
  const categoryNames: Record<string, string> = menuCategories.reduce((acc, category) => {
    acc[category.id.toString()] = category.name;
    return acc;
  }, {} as Record<string, string>);
  
  // Add fallback for uncategorized items
  categoryNames['uncategorized'] = 'Other Items';

  return (
    <div className="fade-in">
      {/* Demo Mode Indicator */}
      {isDemoOrg(orgSlug) && (
        <div className="demo-badge">
          🎯 DEMO MODE
        </div>
      )}

      {/* Hero Section */}
      <section className="hero">
        <h1>
          {cafeInfo.name}
          {isDemoOrg(orgSlug) && (
            <span className="demo-suffix">
              (Demo)
            </span>
          )}
        </h1>
        <p>{cafeInfo.description}</p>
        
        {/* Hidden link - clicking on the cafe name 5 times reveals the client cabinet */}
        <div 
          onClick={(e) => {
            const target = e.target as HTMLElement;
            let clicks = parseInt(target.dataset.clicks || '0');
            clicks++;
            target.dataset.clicks = clicks.toString();
            
            if (clicks >= 5) {
              // Show hidden message with link
              const hiddenLink = document.getElementById('hidden-link');
              if (hiddenLink) {
                hiddenLink.style.display = 'block';
              }
            }
          }}
          style={{ cursor: 'pointer', display: 'inline-block' }}
          title="Click me 5 times for a surprise..."
        >
          <h2>Welcome to Our Cafe</h2>
        </div>
        
        <div id="hidden-link" className="hidden-link">
          <p>
            🎉 You found the secret! Access our internal ordering system:
          </p>
          <Link to={`/${orgSlug}/order`} className="btn btn-warning">
            Client Ordering System
          </Link>
        </div>
      </section>

      {/* Cafe Information */}
      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Visit Us</h2>
        </div>
        
        <div className="cafe-info-grid">
          <div className="cafe-info-section">
            <h3>Location & Contact</h3>
            <p><strong>Address:</strong> {cafeInfo.location}</p>
            <p><strong>Phone:</strong> {cafeInfo.phone}</p>
          </div>
          
          <div className="cafe-info-section">
            <h3>Opening Hours</h3>
            {Object.entries(cafeInfo.hours).map(([day, hours]) => (
              <p key={day}><strong>{day}:</strong> {hours}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Display */}
      <section className={`card ${isDemoOrg(orgSlug) ? 'demo-card' : ''}`}>
        <div className="card-header">
          <h2 className="card-title">
            Our Menu
            {isDemoOrg(orgSlug) && (
              <span className="demo-suffix">
                (Demo Data)
              </span>
            )}
          </h2>
        </div>
        
        {Object.entries(menuByCategory).map(([category, items]) => (
          <div key={category} className="menu-category">
            <h3>{categoryNames[category as keyof typeof categoryNames]}</h3>
            <div className="menu-grid">
              {items.map(item => (
                <div key={item.id} className={`menu-item ${isDemoOrg(orgSlug) ? 'demo-menu-item' : ''}`}>
                  {item.imageUrl && (
                    <div className="menu-item-image">
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          // Show a placeholder for demo items
                          if (isDemoOrg(orgSlug)) {
                            const placeholder = document.createElement('div');
                            placeholder.className = 'menu-item-placeholder';
                            placeholder.textContent = '🍽️';
                            target.parentElement?.appendChild(placeholder);
                          }
                        }}
                      />
                    </div>
                  )}
                  <div className="menu-item-info">
                    <div>
                      <div className="menu-item-name">
                        {item.name}
                      </div>
                      <div className="menu-item-description">
                        {item.description}
                      </div>
                    </div>
                    <div className="menu-item-price">
                      ${item.price.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* About Section */}
      <section className="card">
        <div className="card-header">
          <h2 className="card-title">About Us</h2>
        </div>
        <div className="about-section">
          <p>
            At {cafeInfo.name}, we believe in creating more than just great food – we craft experiences. 
            Our passionate team of chefs and baristas work with the finest locally-sourced ingredients 
            to bring you flavors that celebrate both tradition and innovation. Whether you're stopping 
            by for your morning coffee, a business lunch, or a casual dinner with friends, we're here 
            to make every visit memorable.
          </p>
          <p>
            Come discover your new favorite spot at {cafeInfo.location}. We can't wait to serve you!
          </p>
        </div>
      </section>



      {/* Demo Information Section */}
      {isDemoOrg(orgSlug) && (
        <section className="card demo-info-section">
          <div className="card-header">
            <h2 className="card-title">🎯 Demo Information</h2>
          </div>
          <div className="demo-info-content">
            <p>
              <strong>Welcome to the BeanBuzz Demo!</strong> This is a demonstration of our comprehensive cafe management system.
            </p>
            <div className="demo-features-grid">
              <div className="demo-feature-card primary">
                <h4>📊 Demo Features</h4>
                <ul>
                  <li>18 sample menu items</li>
                  <li>6 menu categories</li>
                  <li>5 demo tables</li>
                  <li>3 sample orders</li>
                </ul>
              </div>
              <div className="demo-feature-card secondary">
                <h4>🔧 System Features</h4>
                <ul>
                  <li>Table ordering system</li>
                  <li>Kitchen display</li>
                  <li>Client cabinet</li>
                  <li>Analytics dashboard</li>
                </ul>
              </div>
            </div>
            <div className="demo-actions">
              <Link to={`/${orgSlug}/order`} className="btn btn-primary">
                🍽️ Try Table Ordering
              </Link>
              <Link
                to={`/${orgSlug}/dashboard`}
                className="btn btn-secondary"
              >
                🏠 Management Dashboard
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Back to Management Dashboard - only show for demo org */}
      {isDemoOrg(orgSlug) && (
        <div className="back-link">
          <Link
            to={`/${orgSlug}/dashboard`}
            className="btn btn-secondary"
          >
            ← Back to Management Dashboard
          </Link>
        </div>
      )}
    </div>
  );
};

export default PublicPage; 